import Head from 'next/head';
import {useRouter} from 'next/router';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import PropTypes from 'prop-types';
import React from 'react';

import Storyblok, {useStoryblok} from '@/src/app/utils/storyblok';
import DynamicComponent from '@/src/dynamicComponents/components/DynamicComponent';
import {HeaderContext} from '@/src/header/context/HeaderContext';
import MainContainer from '@/src/main/container/MainContainer';

import {LANDING_PAGE_SLUG} from './index';
import {PREVIEW_IMAGE_PATH} from './job-portal/position/[slug]';

export default function Page({story, preview, locale, host}) {
  story = useStoryblok(story, preview, locale);
  const {asPath} = useRouter();
  const fullPath = `https://${host}${asPath}`;
  const imagesPath = `https://${host}/${PREVIEW_IMAGE_PATH}`;

  const metaTitle = story.content.title;
  const metaDescription = story.content.description;

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        {/*Open Graph / Facebook*/}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullPath} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={imagesPath} />

        {/*Twitter*/}
        <meta name="twitter:site" content={host} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imagesPath} />
      </Head>
      <div>
        <HeaderContext.Provider
          value={{
            isTransparent: story.content.transparentHeader,
          }}
        >
          <MainContainer
            widthContainer={MainContainer.CONTAINER_WIDTH.FULL_WIDTH}
            topOffset={MainContainer.TOP_OFFSET.OFFSET_0}
          >
            <DynamicComponent blok={story.content} />
          </MainContainer>
        </HeaderContext.Provider>
      </div>
    </>
  );
}

export async function getServerSideProps({
  locale,
  params,
  defaultLocale,
  ...context
}) {
  let slug = params.slug ? params.slug.join('/') : LANDING_PAGE_SLUG;

  const insideStoryblok = context.query._storyblok;
  const shouldLoadDraft = context.preview || insideStoryblok;

  // load the published content outside of the preview mode
  let storyblokParams: IStorblokParams = {
    version: 'publish',
    language: locale,
  };

  if (shouldLoadDraft) {
    storyblokParams.version = 'draft';
    storyblokParams.cv = Date.now();
  }

  try {
    const {data} = await Storyblok.get(`cdn/stories/${slug}`, storyblokParams);

    return {
      props: {
        host: context.req.headers.host,
        locale,
        story: data ? data.story : false,
        preview: shouldLoadDraft || false,
        isDarkMode: data?.story?.content?.darkTheme,
        ...(await serverSideTranslations(locale, ['common'])),
      },
    };
  } catch (error) {
    return {
      redirect: {
        destination: locale === defaultLocale ? '/404' : `/${locale}/404`,
        permanent: true,
        locale: true,
      },
    };
  }
}

Page.propTypes = {
  story: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  preview: PropTypes.bool.isRequired,
};
